import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import { WINDOW } from '@constants/global-objects.constants';
import { ApiError } from '@models/app.models';
import { Booking, Seat } from '@models/booking.models';
import { RudderStackService } from '@services/rudderstack/rudderstack.service';
import { WindowService } from '@services/window/window.service';
import { Observable, Subject, take } from 'rxjs';
import { BBox, BBoxEventProperties, ModalProperties } from 'src/app/typings/bbox';

@Injectable({
  providedIn: 'root',
})
export class BBoxService {
  private rsService: RudderStackService = inject(RudderStackService);
  private _readySubject = new Subject<void>();
  private readonly extraPropertyKey = 'x';
  private readonly experimentViewedEventName = 'experiment_viewed';
  private props: BBoxEventProperties = {};

  public onReady$: Observable<void> = this._readySubject.asObservable();
  public bbox: BBox;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(WINDOW) private readonly window: Window,
  ) {
    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'assets/bbox/bbox.js';
    script.dataset['events'] = 'click select copy cut paste input submit error';
    script.async = true;
    this.document.head.appendChild(script);

    const botScore = window._n0bs ?? -2;
    this.rsService.onReady$.pipe(take(1)).subscribe(() => {
      this.rsTrack('bot', {
        score: botScore,
      });

      this.bbox = WindowService.getBbox();
      this.bbox.props = this.props;
      this.bbox.initialize();

      this._readySubject.next();
    });
  }

  private getExtendedBookingProperties(booking: Booking | null): BBoxEventProperties {
    const bookingProps: BBoxEventProperties = {};
    if (booking !== null) {
      const payments = booking.payments;
      if (payments.length > 0) {
        const lastPayment = payments[payments.length - 1];
        bookingProps['last_payment'] = {
          amount: lastPayment?.amounts?.amount ?? null,
          currency: lastPayment?.amounts?.currencyCode ?? null,
          status: lastPayment?.status ?? null,
          type: lastPayment?.type ?? null,
          created_date: lastPayment?.createdDate ?? null,
          modified_date: lastPayment?.modifiedDate ?? null,
        }
      }
    }
    return bookingProps;
  }

  public trackViewEvent(): void {
    this.bbox.trackView();
  }

  public trackBooking(booking: Booking | null): void {
    let bookingProps = this.bbox.getBookingProperties();
    bookingProps = {
      ...bookingProps,
      ...this.getExtendedBookingProperties(booking),
    };
    this.bbox.track('booking', bookingProps);
  }

  public trackModifyBooking(booking: Booking | null): void {
    let bookingProps = this.bbox.getBookingProperties();
    bookingProps = {
      ...bookingProps,
      ...this.getExtendedBookingProperties(booking),
    };
    this.bbox.track('modify_booking', bookingProps);
  }

  public trackUpgradeBooking(booking: Booking | null): void {
    let bookingProps = this.bbox.getBookingProperties();
    bookingProps = {
      ...bookingProps,
      ...this.getExtendedBookingProperties(booking),
    };
    this.bbox.track('upgrade_booking', bookingProps);
  }

  public trackCancelBooking(booking: Booking | null): void {
    let bookingProps = this.bbox.getBookingProperties();
    bookingProps = {
      ...bookingProps,
      ...this.getExtendedBookingProperties(booking),
    };
    this.bbox.track('cancel_booking', bookingProps);
  }

  public trackBookingSeats(booking: Booking): void {
    booking.seats?.map((seat: Seat) => {
      seat.passengerSeats.map((passenger) => {
        const seatProp = {
          seat: passenger.unitDesignator,
          segmentKey: passenger.segmentKey,
          bookingKey: booking.bookingKey,
          unitKey: passenger.unitKey,
          unitDesignator: passenger.unitDesignator,
          total: passenger.total,
        };
        this.bbox.track('seats', seatProp);
      });
    });
  }

  public trackExperimentViewed(eventProperties: BBoxEventProperties): void {
    eventProperties[this.extraPropertyKey] =
      this.bbox.props[this.extraPropertyKey];
    this.bbox.track(this.experimentViewedEventName, eventProperties);
  }

  public trackModal(modal: ModalProperties): void {
    const defaultProperties = this.bbox.getDefaultProperties();
    this.bbox.track('modal', {
      ...defaultProperties,
      modal: {
        ...modal,
      },
    });
  }

  public trackApiError(error: ApiError): void {
    const defaultProperties = this.bbox.getDefaultProperties();
    this.bbox.track('api_error', {
      ...defaultProperties,
      api_error: {
        code: error.code ?? '',
      },
    });
  }

  public addTrackingProperties(props: BBoxEventProperties): void {
    try {
      this.bbox.props[this.extraPropertyKey] = props;
    } catch {
      this.props[this.extraPropertyKey] = props;
    }
  }

  public rsTrack(eventName: string, eventProps: BBoxEventProperties): void {
    this.rsService.getAnalytics().track(eventName, eventProps);
  }
}
